import { motion } from "framer-motion";
import { 
  Users, 
  Bot, 
  Container, 
  ScanLine, 
  Receipt,
  Ship,
  Plane,
  FileText,
  Calendar
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

export function ShippingOperationsSection() {
  const { t, language } = useLanguage();
  
  const weekDays = language === 'zh' ? ['一', '二', '三', '四', '五', '六', '日'] 
    : language === 'en' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'];

  return (
    <section className="relative py-12 overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <div className="absolute inset-0 rounded-[3rem] overflow-hidden -mx-4 md:mx-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0d9488] via-[#14b8a6] to-[#06b6d4]" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#22d3ee]/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#2dd4bf]/50 rounded-full blur-[100px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0ea5e9]/30 rounded-full blur-[150px]" />
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="relative z-10 py-8 px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t("shipping.title")}
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              {t("shipping.desc")}
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {/* Card 1: CRM */}
            <motion.div variants={itemVariants} className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col h-full">
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="grid grid-cols-4 gap-2 text-sm font-medium text-gray-500 mb-3">
                    <span>{t("shipping.crm.header.customer")}</span>
                    <span>{t("shipping.crm.header.company")}</span>
                    <span>{t("shipping.crm.header.status")}</span>
                    <span>{t("shipping.crm.header.stage")}</span>
                  </div>
                  
                  <div className="space-y-3">
                    <motion.div className="grid grid-cols-4 gap-2 items-center bg-white rounded-lg p-3 shadow-xs" whileHover={{ scale: 1.02, x: 4 }} transition={{ type: "spring", stiffness: 300 }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-xs font-bold">{language === 'zh' ? '李' : 'M'}</div>
                        <span className="text-sm font-medium text-gray-800">{t("shipping.crm.customer1")}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t("shipping.crm.company1")}</span>
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">{t("shipping.crm.status1")}</Badge>
                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">{t("shipping.crm.stage1")}</Badge>
                    </motion.div>
                    
                    <motion.div className="grid grid-cols-4 gap-2 items-center bg-white rounded-lg p-3 shadow-xs" whileHover={{ scale: 1.02, x: 4 }} transition={{ type: "spring", stiffness: 300 }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold">{language === 'zh' ? '王' : 'F'}</div>
                        <span className="text-sm font-medium text-gray-800">{t("shipping.crm.customer2")}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t("shipping.crm.company2")}</span>
                      <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs">{t("shipping.crm.status2")}</Badge>
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">{t("shipping.crm.stage2")}</Badge>
                    </motion.div>
                    
                    <motion.div className="grid grid-cols-4 gap-2 items-center bg-white rounded-lg p-3 shadow-xs" whileHover={{ scale: 1.02, x: 4 }} transition={{ type: "spring", stiffness: 300 }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-xs font-bold">{language === 'zh' ? '张' : 'D'}</div>
                        <span className="text-sm font-medium text-gray-800">{t("shipping.crm.customer3")}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t("shipping.crm.company3")}</span>
                      <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-xs">{t("shipping.crm.status3")}</Badge>
                      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">{t("shipping.crm.stage3")}</Badge>
                    </motion.div>
                    
                    <motion.div className="grid grid-cols-4 gap-2 items-center bg-white rounded-lg p-3 shadow-xs" whileHover={{ scale: 1.02, x: 4 }} transition={{ type: "spring", stiffness: 300 }}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white text-xs font-bold">{language === 'zh' ? '陈' : 'S'}</div>
                        <span className="text-sm font-medium text-gray-800">{t("shipping.crm.customer4")}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t("shipping.crm.company4")}</span>
                      <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">{t("shipping.crm.status4")}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">{t("shipping.crm.stage4")}</Badge>
                    </motion.div>
                  </div>
                </div>
                
                <div className="mt-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-secondary" />
                    <h3 className="text-xl font-bold text-gray-900">{t("shipping.crm.title")}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{t("shipping.crm.desc")}</p>
                </div>
              </div>
            </motion.div>

            {/* Card 2: Digital Workers */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col h-full">
                <div className="space-y-3 mb-4">
                  <motion.div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-3" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Ship className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800">{t("shipping.worker.sea")}</span>
                      <Badge className="ml-2 bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">{t("shipping.worker.seaRole")}</Badge>
                    </div>
                  </motion.div>
                  
                  <motion.div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-3" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <Plane className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800">{t("shipping.worker.air")}</span>
                      <Badge className="ml-2 bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs">{t("shipping.worker.airRole")}</Badge>
                    </div>
                  </motion.div>
                  
                  <motion.div className="flex items-center gap-3 bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-3" whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-800">{t("shipping.worker.doc")}</span>
                      <Badge className="ml-2 bg-teal-100 text-teal-700 hover:bg-teal-100 text-xs">{t("shipping.worker.docRole")}</Badge>
                    </div>
                  </motion.div>
                </div>
                
                <div className="mt-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <Bot className="w-5 h-5 text-secondary" />
                    <h3 className="text-xl font-bold text-gray-900">{t("shipping.worker.title")}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{t("shipping.worker.desc")}</p>
                </div>
              </div>
            </motion.div>

            {/* Card 3: Resource Booking */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col h-full">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <motion.div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center" whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
                    <Container className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <span className="text-xs font-medium text-gray-700">{t("shipping.resource.20ft")}</span>
                    <div className="text-lg font-bold text-orange-600 mt-1">8/10</div>
                    <span className="text-xs text-gray-500">Available</span>
                  </motion.div>
                  
                  <motion.div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center" whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
                    <Container className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <span className="text-xs font-medium text-gray-700">{t("shipping.resource.40ft")}</span>
                    <div className="text-lg font-bold text-blue-600 mt-1">3</div>
                    <span className="text-xs text-gray-500">Available</span>
                  </motion.div>
                </div>
                
                <div className="flex justify-between mb-4 bg-gray-50 rounded-lg p-2">
                  {weekDays.map((day, i) => (
                    <motion.div 
                      key={day + i} 
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-[10px] font-medium ${
                        i === 2 ? 'bg-secondary text-white' : 'text-gray-600 hover:bg-secondary/10'
                      }`}
                      whileHover={{ scale: 1.1 }}
                    >
                      {day}
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-secondary" />
                    <h3 className="text-xl font-bold text-gray-900">{t("shipping.resource.title")}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{t("shipping.resource.desc")}</p>
                </div>
              </div>
            </motion.div>

            {/* Card 4: AI Document Scan */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col h-full">
                <motion.div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4 overflow-hidden" whileHover={{ scale: 1.02 }}>
                  <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-gray-800">{t("shipping.scan.billTitle")}</span>
                      <span className="text-xs text-gray-500">B/L No: SHIP2024001</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t("shipping.scan.origin")}</span>
                        <span className="text-gray-500">{t("shipping.scan.dest")}</span>
                      </div>
                    </div>
                  </div>
                  
                  <motion.div 
                    className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-secondary to-transparent"
                    animate={{ top: ['0%', '100%', '0%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                </motion.div>
                
                <div className="mt-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <ScanLine className="w-5 h-5 text-secondary" />
                    <h3 className="text-xl font-bold text-gray-900">{t("shipping.scan.title")}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{t("shipping.scan.desc")}</p>
                </div>
              </div>
            </motion.div>

            {/* Card 5: AI Fee Settlement */}
            <motion.div variants={itemVariants} className="bg-white rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="flex flex-col h-full">
                <motion.div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 mb-4" whileHover={{ scale: 1.02 }}>
                  <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-gray-800">{t("shipping.fee.title")}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">{t("shipping.fee.verified")}</Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">{t("shipping.fee.port")}</span>
                        <span className="font-medium text-gray-800">¥ 2,450</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">{t("shipping.fee.insurance")}</span>
                        <span className="font-medium text-gray-800">¥ 1,200</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">{t("shipping.fee.transport")}</span>
                        <span className="font-medium text-gray-800">¥ 15,800</span>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-xs font-medium text-gray-700">{t("shipping.fee.total")}</span>
                        <span className="text-sm font-bold text-emerald-600">¥ 19,450</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>{t("shipping.fee.linked")} #SH2024-0892</span>
                  </div>
                </motion.div>
                
                <div className="mt-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-5 h-5 text-secondary" />
                    <h3 className="text-xl font-bold text-gray-900">{t("shipping.fee.cardTitle")}</h3>
                  </div>
                  <p className="text-gray-600 text-sm">{t("shipping.fee.cardDesc")}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
